import { ajaxSend } from 'api/api';
import type { NodeT } from 'fable/flowEditor';

import type {
  FlowCreatePayloadT,
  FlowsCreateResponseT,
  FlowsGetItemResponseT,
  FlowsGetItemsResponseT,
  FlowsQueryParams,
  FlowsRemoveResponseT,
  FlowsUpdateResponseT,
  FlowT,
} from './types';

const FLOW_API_BASE_V1 = '/v1/flows';
const FLOW_API_BASE_V2 = '/v2/flows';

class FlowsApi {
  /**
   * Creates a new flow.
   *
   * @param {FlowCreatePayloadT<TNode>} data The create flow data object.
   */
  public createFlow<TNode>(data: FlowCreatePayloadT<TNode>) {
    return ajaxSend<FlowsCreateResponseT<TNode>>({
      data: data as FlowCreatePayloadT<TNode>,
      method: 'POST',
      path: FLOW_API_BASE_V1,
    });
  }

  /**
   * Gets a single flow.
   *
   * @param {string} flowId The id of the flow to be retrieved.
   */
  public getFlow<TNode = NodeT>(flowId: string) {
    return ajaxSend<FlowsGetItemResponseT<TNode>>({
      path: `${FLOW_API_BASE_V1}/${flowId}`,
    });
  }

  /**
   * Gets flows.
   *
   * @param {FlowsQueryParams} params The get flows query params object.
   */
  public getFlows<TNode = NodeT>(params: FlowsQueryParams) {
    return ajaxSend<FlowsGetItemsResponseT<TNode>>({
      params: params as FlowsQueryParams,
      path: `${FLOW_API_BASE_V2}`,
    });
  }

  /**
   * Updates flow.
   *
   * @param {string} flowId The id of the flow to be updated.
   * @param {Partial<FlowT<TNode>>} flow The params of flow to be updated.
   */
  public updateFlow<TNode>(flowId: string, flow: FlowT<TNode>) {
    return ajaxSend<FlowsUpdateResponseT<TNode>>({
      data: flow as Partial<FlowT<TNode>>,
      method: 'PUT',
      path: `${FLOW_API_BASE_V1}/${flowId}`,
    });
  }

  /**
   * Removes flow.
   *
   * @param {string} flowId The id of the flow to be removed.
   */
  public removeFlow<TNode>(flowId: string) {
    return ajaxSend<FlowsRemoveResponseT<TNode>>({
      method: 'DELETE',
      path: `${FLOW_API_BASE_V1}/${flowId}`,
    });
  }
}

export const flowsApi = new FlowsApi();
